import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Divider, IconButton, Stack, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

import Modal from "components/basic/modal";
import Typography from "components/basic/typography";
import { formatMoney } from "utils/formatMoney";
import { LeadCustomer, QuoteSummary } from "../../types";

interface QuoteDetailsDialogProps {
  customer: LeadCustomer;
  onClose: () => void;
}

const Line: React.FC<{
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}> = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography
      variant="body2"
      style={{ color: "inherit", fontWeight: bold ? 600 : 400 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" style={{ fontWeight: bold ? 700 : 500 }}>
      {value}
    </Typography>
  </Stack>
);

const TextBlock: React.FC<{
  label: string;
  value?: string | string[] | null;
}> = ({ label, value }) => {
  const theme = useTheme();
  const items = Array.isArray(value)
    ? value.filter(Boolean)
    : value
      ? [value]
      : [];
  if (items.length === 0) {
    return null;
  }
  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        style={{ color: theme.palette.text.secondary, fontWeight: 600 }}
      >
        {label}
      </Typography>
      {items.length === 1 ? (
        <Typography variant="body2">{items[0]}</Typography>
      ) : (
        <ul style={{ margin: "2px 0", paddingLeft: 18 }}>
          {items.map((item, idx) => (
            <li key={idx}>
              <Typography variant="body2">{item}</Typography>
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

const QuoteCard: React.FC<{ quote: QuoteSummary; index: number }> = ({
  quote,
  index,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {quote.estimateNumber
            ? `Estimate ${quote.estimateNumber}`
            : `Quote #${index + 1}`}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {quote.stage && (
            <Chip size="small" label={quote.stage} variant="outlined" />
          )}
          {quote.createdAt && (
            <Typography
              variant="caption"
              style={{ color: theme.palette.text.secondary }}
            >
              {moment(Number(quote.createdAt)).format("MMM D, YYYY")}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        {quote.chargerModel && (
          <Line
            label={t("quotes_dialog.charger_model", {
              model: quote.chargerModel,
            })}
            value={formatMoney(quote.chargerCost)}
          />
        )}
        {(quote.addOns ?? []).map((addOn, index) => (
          <Line
            key={`${addOn.label}-${index}`}
            label={t("quotes_dialog.add_on", { label: addOn.label })}
            value={formatMoney(addOn.price)}
          />
        ))}
        {quote.installationCost != null && (
          <Line
            label={t("quotes_dialog.installation")}
            value={formatMoney(quote.installationCost)}
          />
        )}
        {quote.subtotal != null && (
          <Line
            label={t("quotes_dialog.subtotal")}
            value={formatMoney(quote.subtotal)}
          />
        )}
        {quote.taxAmount != null && (
          <Line
            label={`${
              quote.taxLabel
                ? t("quotes_dialog.tax_label", { label: quote.taxLabel })
                : t("quotes_dialog.tax")
            }${quote.taxRate != null ? ` · ${quote.taxRate}%` : ""}`}
            value={formatMoney(quote.taxAmount)}
          />
        )}
        <Divider sx={{ my: 0.5 }} />
        <Line
          label={t("quotes_dialog.total")}
          value={formatMoney(quote.totalCost)}
          bold
        />
      </Stack>

      <TextBlock
        label={t("quotes_dialog.inclusions")}
        value={quote.inclusions}
      />
      <TextBlock
        label={t("quotes_dialog.assumptions")}
        value={quote.assumptions}
      />
      <TextBlock
        label={t("quotes_dialog.payment_terms")}
        value={quote.paymentTerms}
      />
      <TextBlock label={t("quotes_dialog.notes")} value={quote.notes} />
    </Box>
  );
};

const QuoteDetailsDialog: React.FC<QuoteDetailsDialogProps> = ({
  customer,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");

  return (
    <Modal onClose={onClose}>
      <Box
        sx={{
          width: { xs: "88vw", sm: 460 },
          maxHeight: "86vh",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            mb: 1,
          }}
        >
          <Typography variant="h4" style={{ fontWeight: 600 }}>
            {t("quotes_dialog.title")}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: "absolute", right: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            {customer.name || customer.email}
          </Typography>
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {[customer.email, customer.phone, customer.address]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>

        {customer.quotes.length === 0 ? (
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary }}
          >
            {t("quotes_dialog.none")}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {customer.quotes.map((q, idx) => (
              <QuoteCard key={q.id} quote={q} index={idx} />
            ))}
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default QuoteDetailsDialog;
